import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { updateShopSettings, getMyShopPrivate } from '../../services/shop-service';
import { useAuthStore } from '../../store/authStore';
import { normalizeAddress } from '../../lib/shop-address';

// Common IANA timezones — selectable list to keep the UI simple. Server
// validates against pg_timezone_names regardless.
const COMMON_TIMEZONES = [
  'UTC',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Pacific/Auckland',
];

interface AnalyticsPreferencesProps {
  // Hub-landing rendering (mockup #8b's "Share anonymised stats" row): just
  // the shop-directory toggle, saved instantly on flip instead of behind a
  // separate SAVE PREFERENCES button. Sends a MINIMAL payload (shopId +
  // discovery_listed only) — see handleToggleDiscovery below. It must NOT
  // reuse the full form's handleSave, which sends at_risk_days/timezone/
  // avg_coffee_price_cents alongside — if getMyShopPrivate() had failed
  // silently (it swallows errors to null) those would still be in state at
  // their stale defaults, and one hub tap would overwrite the owner's real
  // values with them.
  compact?: boolean;
}

// Teal pill / white knob per the coral toggle spec, shared by both the full
// form's DISCOVERY row and the compact hub row.
function DiscoveryToggle({ checked, onClick, disabled }: { checked: boolean; onClick: () => void; disabled: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label="Shop directory listing"
      onClick={onClick}
      disabled={disabled}
      className={`flex-none w-[52px] h-[30px] rounded-full p-[3px] flex disabled:opacity-50 transition-colors ${
        checked ? 'bg-pixel-cyan justify-end' : 'bg-hairline justify-start'
      }`}
    >
      <span className="w-6 h-6 rounded-full bg-white" />
    </button>
  );
}

export default function AnalyticsPreferences({ compact = false }: AnalyticsPreferencesProps) {
  const { userAddress } = useAuthStore();
  const [shopId, setShopId] = useState<string | null>(null);
  const [atRiskDays, setAtRiskDays] = useState(14);
  const [timezone, setTimezone] = useState('UTC');
  const [avgPriceCents, setAvgPriceCents] = useState(500);
  const [discoveryListed, setDiscoveryListed] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userAddress) return;
    let cancelled = false;
    (async () => {
      // avg_coffee_price_cents is revoked from `authenticated` (045) and must
      // not appear in this projection — Postgres would reject the whole query.
      // It comes back separately through the owner-scoped RPC.
      const [{ data }, priv] = await Promise.all([
        supabase
          .from('shops')
          .select('id, at_risk_days, timezone, discovery_listed')
          .eq('owner_address', normalizeAddress(userAddress))
          .single(),
        getMyShopPrivate(),
      ]);
      if (cancelled) return;
      if (data) {
        setShopId(data.id);
        setAtRiskDays(data.at_risk_days || 14);
        setTimezone(data.timezone || 'UTC');
        setDiscoveryListed(data.discovery_listed !== false);
      }
      if (priv?.avg_coffee_price_cents) setAvgPriceCents(priv.avg_coffee_price_cents);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userAddress]);

  async function handleSave() {
    if (!shopId || saving) return;
    setSaving(true);
    setError(null);
    // updateShopSettings throws the server's own reason now, so the catch
    // shows that instead of guessing at the timezone. Without a catch here the
    // rejection would escape the click handler and leave the button on
    // "Saving…" forever — the two calls in this file are the only
    // updateShopSettings sites not already wrapped by a section save.
    try {
      await updateShopSettings({
        shopId,
        at_risk_days: atRiskDays,
        timezone,
        avg_coffee_price_cents: avgPriceCents,
        discovery_listed: discoveryListed,
      });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed. Check your timezone is valid.');
    } finally {
      setSaving(false);
    }
  }

  // Compact-only save path (review fix, round 1): a MINIMAL payload —
  // shopId + discovery_listed ONLY. Deliberately does not go through
  // handleSave, which would also send the current at_risk_days/timezone/
  // avg_coffee_price_cents state — if getMyShopPrivate() had silently
  // failed (it swallows errors to null on load, see the effect above),
  // avgPriceCents would still be sitting at its 500-cent default, and
  // routing through handleSave would overwrite the owner's real price with
  // that stale default on a single hub tap. Reverts the toggle and shows an
  // error inline on failure, rather than leaving the pill flipped while the
  // DB disagrees.
  async function handleToggleDiscovery() {
    if (!shopId || saving) return;
    const next = !discoveryListed;
    setDiscoveryListed(next);
    setSaving(true);
    setError(null);
    try {
      await updateShopSettings({ shopId, discovery_listed: next });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (err) {
      setDiscoveryListed(!next); // revert — DB write failed, don't show a state that isn't real
      setError(err instanceof Error ? err.message : 'Save failed. Try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="bg-white rounded-[10px] h-32 animate-pulse" />;
  }

  if (!shopId) return null;

  // Compact: mockup #8b's "Share anonymised stats" row on the SETUP hub
  // landing. Reuses the exact same discoveryListed state/save call as the
  // full form below — the mockup's "powers the regulars and at-risk lists"
  // copy doesn't match what this flag actually does (it's the customer-
  // facing shop directory listing, not an analytics data-sharing switch), so
  // this keeps the app's own real copy rather than inventing a mismatched
  // description for a toggle with the same name.
  if (compact) {
    return (
      <div className="bg-white rounded-[10px] p-[18px]">
        <div className="flex items-center justify-between gap-3.5">
          <div className="min-w-0">
            <p className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Shop directory listing</p>
            <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey">
              Listed shops appear in every customer's Find a shop directory
            </p>
          </div>
          <DiscoveryToggle checked={discoveryListed} onClick={handleToggleDiscovery} disabled={saving} />
        </div>
        {error && <p className="font-grotesk font-medium text-xs text-error mt-2.5">{error}</p>}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[10px] p-[18px]">
      <h3 className="font-grotesk font-bold text-[17px] text-primary mb-[18px]">
        Analytics preferences
      </h3>

      <div className="space-y-5">
        <div>
          <p className="font-heading text-[9px] text-body-grey uppercase mb-2">
            At-risk threshold: <span className="text-coral">{atRiskDays} days</span>
          </p>
          <p className="font-grotesk font-medium text-[13px] text-body-grey mb-3">
            Customers who haven't visited in this many days appear in your "At-Risk" list so you can win them back.
          </p>
          <input
            type="range"
            min={7}
            max={90}
            step={1}
            value={atRiskDays}
            onChange={(e) => setAtRiskDays(parseInt(e.target.value, 10))}
            className="w-full accent-coral"
          />
          <div className="flex justify-between font-heading text-[8px] text-body-grey mt-1">
            <span>7D</span><span>30D</span><span>60D</span><span>90D</span>
          </div>
        </div>

        <div>
          <p className="font-heading text-[9px] text-body-grey uppercase mb-2">Shop timezone</p>
          <p className="font-grotesk font-medium text-[13px] text-body-grey mb-3">
            Your daily stats roll up at midnight in this timezone. Pick the city closest to your shop.
          </p>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full p-3 bg-background rounded-[8px] border-2 border-hairline focus:border-coral outline-none text-primary text-sm font-grotesk"
          >
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        <div>
          <p className="font-heading text-[9px] text-body-grey uppercase mb-2">Avg coffee price</p>
          <p className="font-grotesk font-medium text-[13px] text-body-grey mb-3">
            Powers the "est. spend" figures in your analytics. Estimate only — never charged to anyone.
          </p>
          <div className="flex items-center gap-2">
            <span className="font-grotesk font-bold text-sm text-primary">$</span>
            <input type="number" min={1} max={50} step={0.5}
              value={(avgPriceCents / 100).toFixed(2)}
              onChange={(e) => setAvgPriceCents(Math.round(parseFloat(e.target.value || '5') * 100))}
              className="w-32 p-3 bg-background rounded-[8px] border-2 border-hairline focus:border-coral outline-none text-primary text-sm font-grotesk" />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3.5">
          <div className="min-w-0">
            <p className="font-heading text-[9px] text-body-grey uppercase mb-2">Shop directory listing</p>
            <p className="font-grotesk font-medium text-[13px] text-body-grey">
              Listed shops appear in every customer's Find a shop directory. Turn this off to be
              reachable only by your QR code or link — existing members keep full access either way.
            </p>
          </div>
          <DiscoveryToggle checked={discoveryListed} onClick={() => setDiscoveryListed(v => !v)} disabled={saving} />
        </div>

        {error && <p className="font-grotesk font-medium text-sm text-error">{error}</p>}

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 disabled:opacity-50"
        >
          {saving ? 'Saving…' : savedAt ? 'Saved ✓' : 'Save preferences'}
        </button>
      </div>
    </div>
  );
}
